import { useEffect, useRef, useState } from 'react';
import { isHttpsUrl, normalizeEmbedUrl, isVideoEmbed, addAutoplay, isScriptEmbed } from '../model/pages';

/** The sealed page script embeds run in (served by routes/pagesPublic.js). */
export const EMBED_FRAME_PATH = '/api/public/pages/embed-frame';

/** Sandboxed https-only iframe embed (Calendly/Loom/YouTube/Vimeo/Wistia/etc.).
 *  Share links are normalized to their embeddable player URL. Video embeds render
 *  in a responsive 16:9 box (scales to the container); other embeds keep a fixed
 *  min-height. Renders nothing for bad URLs. `autoplay` (the video poster's
 *  click-to-play) appends the provider's autoplay flag after normalizing. */
export default function EmbedBlock({ url, title = 'Embedded content', minHeight = 640, autoplay }: { url?: string; title?: string; minHeight?: number; autoplay?: boolean }) {
  if (isScriptEmbed(url)) return <ScriptEmbed code={String(url).trim()} title={title} />;
  const src = autoplay ? addAutoplay(normalizeEmbedUrl(url)) : normalizeEmbedUrl(url);
  if (!isHttpsUrl(src)) return null;
  const video = isVideoEmbed(src);
  const common = {
    src, title, loading: 'lazy' as const,
    // Video players (YouTube especially) verify the embedding domain via the
    // Referer/origin — stripping it triggers "Video unavailable" / error 150/153.
    // So send the origin for videos; keep no-referrer for other embeds.
    referrerPolicy: (video ? 'strict-origin-when-cross-origin' : 'no-referrer') as React.HTMLAttributeReferrerPolicy,
    allow: 'autoplay; fullscreen; picture-in-picture; encrypted-media',
    allowFullScreen: true,
    sandbox: 'allow-scripts allow-forms allow-popups allow-same-origin allow-presentation',
  };
  if (video) {
    return (
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9' }}>
        <iframe {...common} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, borderRadius: 12 }} />
      </div>
    );
  }
  return <iframe {...common} style={{ width: '100%', minHeight, border: 0, borderRadius: 12 }} />;
}

/**
 * Embed code that runs its own scripts (a widget snippet). It runs in a frame of its own:
 * a page this app serves with an opaque origin (CSP sandbox, no allow-same-origin), so the
 * snippet cannot reach the page, the app or anyone's session. The page hands the code over
 * by message once the frame says it is ready (the frame may load before hydration), and
 * the frame reports its height so the box fits the widget.
 */
function ScriptEmbed({ code, title }: { code: string; title: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    const win = el?.ownerDocument.defaultView;
    if (!el || !win) return;
    const send = () => el.contentWindow?.postMessage({ type: 'nla-embed-code', code }, '*');
    const onMessage = (e: MessageEvent) => {
      if (!e.source || e.source !== el.contentWindow) return;
      const d = e.data as { type?: unknown; height?: unknown } | null;
      if (d?.type === 'nla-embed-ready') send();
      else if (d?.type === 'nla-embed-height') {
        const h = Number(d.height);
        if (Number.isFinite(h) && h > 0) setHeight(Math.min(4000, Math.max(60, Math.ceil(h))));
      }
    };
    win.addEventListener('message', onMessage);
    send();
    return () => win.removeEventListener('message', onMessage);
  }, [code]);
  return (
    <iframe ref={ref} src={EMBED_FRAME_PATH} title={title} loading="lazy"
      sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen
      style={{ display: 'block', width: '100%', height: height ?? 360, border: 0, borderRadius: 12 }} />
  );
}
